import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Message, SelectPicker } from 'rsuite';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../utils/toast';
import {
  usePeriodAction,
  usePreviousPeriodTasks,
  useSaveTimesheetEntry,
  useTimesheetConfig,
  useTimesheetEmployees,
  useTimesheetEntries,
  useTimesheetPeriod,
  useTimesheetTasks,
  useTimesheetTimer,
  useTimesheetValidation,
} from '../hooks/useTimesheet';
import {
  Banner,
  ProviderBanner,
  DateStepper,
  TimesheetNav,
  ValidationBanners,
  addToClock,
  suggestStartTime,
} from '../components/timesheet/TimesheetParts';
import PeriodGrid from '../components/timesheet/PeriodGrid';
import WeekColumns from '../components/timesheet/WeekColumns';
import PeriodCard from '../components/timesheet/PeriodCard';
import DayView from '../components/timesheet/DayView';
import EntryModal from '../components/timesheet/EntryModal';
import LogTimeBar from '../components/timesheet/LogTimeBar';
import PeriodActionModal from '../components/timesheet/PeriodActionModal';
import SlideSwap from '../components/timesheet/SlideSwap';
import {
  addDays,
  formatDayHeading,
  formatHM,
  formatPeriod,
  formatRange,
  formatTimestamp,
  groupValidationIssues,
  breakClock,
  formatDate,
  isValidIso,
  isWeekend,
  listDays,
  nextPeriod,
  periodFor,
  previousPeriod,
  todayIso,
  weekFor,
} from '../utils/timesheet';
import './Timesheet.css';
import './TimesheetLayout.css';

const VIEWS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'period', label: 'Pay period' },
];

const PERIOD_ACTIONS = {
  submit: {
    title: 'Submit for Approval',
    confirmLabel: 'Submit',
    commentLabel: 'Note for your reviewer',
    done: 'Timesheet submitted for approval.',
  },
  approve: {
    title: 'Approve Timesheet',
    confirmLabel: 'Approve',
    commentLabel: 'Comment',
    done: 'Timesheet approved.',
  },
  return: {
    title: 'Return Timesheet',
    confirmLabel: 'Return',
    confirmColor: 'orange',
    commentLabel: 'What needs to change?',
    commentRequired: true,
    done: 'Timesheet returned.',
  },
  reopen: {
    title: 'Reopen Timesheet',
    confirmLabel: 'Reopen',
    commentLabel: 'Reason',
    done: 'Timesheet reopened.',
  },
};

/**
 * My Timesheet.
 *
 * A range bar for how you are looking (day, week or pay period) and where, with
 * the totals for each; under it the view on the left - the day as an agenda,
 * the week as a column per day, the pay period as a heat map of task by day -
 * and a sidebar on the right with the timer / log time card and the pay period
 * card, where submitting and reviewing happen (per pay period, the unit
 * payroll runs on).
 *
 * The page also serves reviewers and managers looking at somebody else's
 * timesheet (?employee=<id>); what they can do there is whatever the server's
 * period `actions` say, so the page never has to second-guess permissions.
 */
export default function TimesheetPage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayIso();

  // `date` is the anchor; `start` and `day` are older links' names for it.
  const rawDate = [searchParams.get('date'), searchParams.get('day'), searchParams.get('start')].find(isValidIso);
  const date = rawDate || today;
  const view = VIEWS.some((v) => v.key === searchParams.get('view')) ? searchParams.get('view') : 'day';
  const period = useMemo(() => periodFor(date), [date]);
  const week = useMemo(() => weekFor(date), [date]);
  const visibleDays = useMemo(
    () => (view === 'week' ? listDays(week.start, week.end) : listDays(period.start, period.end)),
    [view, week, period]
  );
  // One fetch covers the day, its week and its pay period, whichever is shown.
  const fetchStart = week.start < period.start ? week.start : period.start;
  const fetchEnd = week.end > period.end ? week.end : period.end;

  const employeeParam = searchParams.get('employee') || '';
  const isSelf = !employeeParam || employeeParam === String(auth.employee?.id || '');
  const employeeId = isSelf ? undefined : employeeParam;

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    ['start', 'day'].forEach((legacy) => next.delete(legacy));
    if (!next.get('date') && rawDate) next.set('date', rawDate);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  };
  const goTo = (iso) => updateParams({ date: iso === today ? null : iso, highlight: null });

  const step = (dir) => {
    if (view === 'day') goTo(addDays(date, dir));
    else if (view === 'week') goTo(addDays(date, dir * 7));
    else goTo((dir > 0 ? nextPeriod(period) : previousPeriod(period)).start);
  };

  const configQuery = useTimesheetConfig();
  const config = configQuery.data;
  const installed = config?.installed !== false;
  const perms = config?.permissions || {};
  const canPickEmployee = perms.canApprove || perms.canReportAll || perms.canManage;

  const tasksQuery = useTimesheetTasks({ includeArchived: true }, { enabled: installed });
  const entriesQuery = useTimesheetEntries({ employeeId, start: fetchStart, end: fetchEnd }, { enabled: installed });
  const periodQuery = useTimesheetPeriod({ employeeId, start: period.start }, { enabled: installed });
  // The View Hours checks over the pay period on screen - the unit they are
  // written for, so the period-wide rules (billable overtime) mean the same.
  const validationQuery = useTimesheetValidation(
    { employeeId, start: period.start, end: period.end },
    { enabled: installed && Boolean(config) }
  );
  const employeesQuery = useTimesheetEmployees({ enabled: Boolean(canPickEmployee) });
  // Shared cache with the entry bar; only needed here to keep a running timer
  // reachable when the period on screen is locked.
  const timerQuery = useTimesheetTimer({ enabled: installed && isSelf });
  const periodAction = usePeriodAction();
  const previousTasks = usePreviousPeriodTasks();
  const saveEntry = useSaveTimesheetEntry();

  const tasks = useMemo(() => tasksQuery.data?.tasks || [], [tasksQuery.data]);
  const taskById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const entries = useMemo(() => entriesQuery.data?.entries || [], [entriesQuery.data]);
  const periodData = periodQuery.data;
  const issues = useMemo(() => groupValidationIssues(validationQuery.data?.validations), [validationQuery.data]);
  const issueCount = issues.failCount + issues.warnCount;
  const actions = periodData?.actions || {};
  const editable = Boolean(actions.canEdit) && installed;
  const status = periodData?.period?.status || 'open';

  // Rows added by hand in the grids that have no time yet. A plan for this
  // sheet only, so they reset when the period or the person changes.
  const [extraTaskIds, setExtraTaskIds] = useState([]);
  useEffect(() => {
    setExtraTaskIds([]);
  }, [period.start, employeeParam]);

  const entriesForDate = useCallback((d) => entries.filter((e) => e.date === d), [entries]);
  const visibleEntries = useMemo(
    () => entries.filter((e) => e.date >= visibleDays[0] && e.date <= visibleDays[visibleDays.length - 1]),
    [entries, visibleDays]
  );

  const cellMap = useMemo(() => {
    const map = new Map();
    visibleEntries.forEach((e) => {
      const key = `${e.taskId}|${e.date}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  }, [visibleEntries]);

  const rowTaskIds = useMemo(() => {
    const logged = [...new Set(visibleEntries.map((e) => e.taskId))];
    logged.sort((a, b) => (taskById.get(a)?.path || '').localeCompare(taskById.get(b)?.path || '', undefined, { numeric: true }));
    return [...logged, ...extraTaskIds.filter((id) => !logged.includes(id))];
  }, [visibleEntries, extraTaskIds, taskById]);

  // The pay period's figures, for the submit bar, whatever the view.
  const periodEntries = entries.filter((e) => e.date >= period.start && e.date <= period.end);
  const periodSeconds = periodEntries.reduce((s, e) => s + e.durationSeconds, 0);
  const periodBillableSeconds = periodEntries.reduce((s, e) => s + (e.billable ? e.durationSeconds : 0), 0);
  const sumSeconds = (from, to) => entries
    .filter((e) => e.date >= from && e.date <= to)
    .reduce((s, e) => s + e.durationSeconds, 0);
  const stats = [
    { label: date === today ? 'Today' : formatDate(date, { weekday: 'short', month: 'short', day: 'numeric' }), seconds: sumSeconds(date, date) },
    { label: 'Week', seconds: sumSeconds(week.start, week.end) },
    { label: 'Pay period', seconds: periodSeconds },
  ];
  const missingDays = listDays(period.start, period.end)
    .filter((d) => !isWeekend(d) && d <= today)
    .filter((d) => !periodEntries.some((e) => e.date === d));

  // Entry modal: { entry } to edit, { seed } to add.
  const [modal, setModal] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [actionError, setActionError] = useState(null);

  const seedFor = (d, extra = {}) => ({ date: d, startTime: suggestStartTime(entriesForDate(d)), ...extra });

  const openCell = (taskId, d, cell) => {
    if (cell.length === 1) setModal({ entry: cell[0] });
    else if (cell.length > 1) updateParams({ view: null, date: d, highlight: taskId });
    else if (editable) setModal({ seed: seedFor(d, { taskId }) });
  };

  const copyPreviousRows = async () => {
    try {
      const result = await previousTasks.mutateAsync({ employeeId, start: period.start });
      const usable = (result.taskIds || []).filter((id) => {
        const t = taskById.get(id);
        return t && !t.effectiveArchived && t.systemRole !== 'paid_time_off';
      });
      if (usable.length === 0) {
        showToast('info', 'Nothing was logged last period to copy.');
        return;
      }
      setExtraTaskIds((prev) => [...new Set([...prev, ...usable])]);
      showToast('success', `Added ${usable.length} row${usable.length === 1 ? '' : 's'} from ${formatPeriod(result.period)}.`);
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  // Same task, note and length, starting where the day's last entry ended. If
  // that would run past midnight it keeps the original's times instead.
  const duplicateEntry = async (entry) => {
    const timed = Boolean(entry.startTime && entry.endTime);
    const start = timed ? suggestStartTime(entriesForDate(entry.date)) : null;
    const end = start ? addToClock(start, entry.durationSeconds) : null;
    try {
      await saveEntry.mutateAsync({
        employeeId,
        taskId: entry.taskId,
        date: entry.date,
        durationSeconds: entry.durationSeconds,
        startTime: timed ? (end ? start : entry.startTime) : null,
        endTime: timed ? (end || entry.endTime) : null,
        description: entry.description || '',
        rdEligible: entry.rdEligible,
      });
      showToast('success', 'Entry duplicated.');
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  // One-click fixes for the checks that have one (see GET /timesheet/validation).
  // Missing breaks go on the task of that type - zero-length markers, or real
  // minutes when the rule counts minutes - each placed where it falls due;
  // rounding moves the end time with the length when there is one. Locked
  // entries are left alone - the server would refuse them anyway.
  const [fixing, setFixing] = useState(null);
  const applyFix = async (issue) => {
    const { fix } = issue;
    if (!fix || fixing) return;
    setFixing(issue.key);
    try {
      if (fix.kind === 'add_breaks') {
        const breakTask = tasks.find((t) => t.systemRole === fix.type && !t.effectiveArchived);
        const typeName = config?.systemRoles?.find((r) => r.key === fix.type)?.label || 'that type';
        if (!breakTask) {
          showToast('warning', `No task has the ${typeName} type. Set one under Admin > Configuration > Timesheet.`);
          return;
        }
        const dayEntries = entriesForDate(issue.date);
        const seconds = (Number(fix.minutes) || 0) * 60;
        for (let i = 0; i < fix.count; i += 1) {
          const at = breakClock(dayEntries, taskById, fix.dueHours?.[i] ?? (i + 1) * 5);
          const end = at && seconds ? addToClock(at, seconds) : at;
          await saveEntry.mutateAsync({
            employeeId,
            taskId: breakTask.id,
            date: issue.date,
            durationSeconds: seconds,
            startTime: at && end ? at : null,
            endTime: at && end ? end : null,
            description: '',
          });
        }
        showToast('success', fix.count === 1 ? `${typeName} added.` : `${fix.count} × ${typeName} added.`);
      } else if (fix.kind === 'set_duration') {
        const entry = entries.find((e) => e.id === fix.entryId);
        if (!entry || entry.locked) return;
        const end = entry.startTime && entry.endTime ? addToClock(entry.startTime, fix.seconds) : null;
        await saveEntry.mutateAsync({
          id: entry.id,
          durationSeconds: fix.seconds,
          ...(entry.startTime && entry.endTime
            ? { startTime: end ? entry.startTime : null, endTime: end }
            : {}),
        });
        showToast('success', `Rounded to ${formatHM(fix.seconds)}.`);
      }
    } catch (err) {
      showToast('error', String(err?.message || err));
    } finally {
      setFixing(null);
    }
  };

  const runAction = async (comment) => {
    const meta = PERIOD_ACTIONS[actionKey];
    setActionError(null);
    try {
      // Submitting defaults to the caller; the review actions always name whose
      // timesheet it is.
      const targetId = isSelf ? (actionKey === 'submit' ? undefined : auth.employee?.id) : employeeParam;
      await periodAction.mutateAsync({ action: actionKey, employeeId: targetId, start: period.start, comment: comment || undefined });
      showToast('success', meta.done);
      setActionKey(null);
    } catch (err) {
      setActionError(String(err?.message || err));
    }
  };

  const employeeName = entriesQuery.data?.employee?.name;
  const employeeOptions = useMemo(
    () => (employeesQuery.data?.employees || [])
      .filter((e) => e.isActive || e.id === employeeParam)
      .map((e) => ({ value: e.id, label: e.name })),
    [employeesQuery.data, employeeParam]
  );

  const loadError = configQuery.error || tasksQuery.error || entriesQuery.error || periodQuery.error;
  const p = periodData?.period;

  // Moving to another day (week, pay period) slides the heading and the
  // entries over toward the side we moved to; switching views just swaps.
  // Worked out during render so the first frame already has it.
  const anchor = view === 'day' ? date : view === 'week' ? week.start : period.start;
  const slideKey = `${view}:${anchor}`;
  const slide = useRef({ key: slideKey, view, anchor, dir: 0 });
  if (slide.current.key !== slideKey) {
    const sameView = slide.current.view === view;
    slide.current = { key: slideKey, view, anchor, dir: sameView ? (anchor > slide.current.anchor ? 1 : -1) : 0 };
  }
  const slideDir = slide.current.dir;

  const label = view === 'day'
    ? formatDayHeading(date)
    : view === 'week'
      ? formatRange(week.start, week.end)
      : `Pay period, ${formatPeriod(period)}`;

  // Only worth a banner when the period is in somebody's hands: submitted,
  // approved, or sent back with something to fix.
  const statusBanner = (() => {
    if (!p || status === 'open') return null;
    if (status === 'submitted') {
      return { tone: 'info', icon: 'bi-send', text: `Submitted ${formatTimestamp(p.submittedAt)}${p.submittedBy ? ` by ${p.submittedBy}` : ''}. Waiting for approval, so this period's time is locked.` };
    }
    if (status === 'approved') {
      return { tone: 'good', icon: 'bi-check-circle', text: `Approved ${formatTimestamp(p.reviewedAt)}${p.reviewedBy ? ` by ${p.reviewedBy}` : ''}.${p.reviewComment ? ` “${p.reviewComment}”` : ''}` };
    }
    return { tone: 'warn', icon: 'bi-arrow-counterclockwise', text: `Returned ${formatTimestamp(p.reviewedAt)}${p.reviewedBy ? ` by ${p.reviewedBy}` : ''}: “${p.reviewComment || 'No comment'}”. Fix it up and submit again.` };
  })();

  return (
    <div className="bdc-page ts-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Timesheet' }]}
        icon="bi-stopwatch"
        title={isSelf ? 'Timesheet' : `${employeeName || 'Employee'}’s Timesheet`}
        actions={<TimesheetNav config={config} />}
        showBack={!isSelf}
        backTo="/timesheet/approvals"
        backLabel="Approvals"
      />

      <ProviderBanner config={config} />

      <div className="ts-rangebar">
        <div className="ts-rangebar__row">
          <div className="ts-viewtabs" role="tablist" aria-label="View">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                type="button"
                role="tab"
                className="ts-viewtabs__tab"
                aria-selected={view === v.key}
                onClick={() => updateParams({ view: v.key === 'day' ? null : v.key, highlight: null })}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="ts-rangebar__tools">
            {canPickEmployee ? (
              <div className="ts-rangebar__person">
                <SelectPicker
                  data={employeeOptions}
                  value={isSelf ? (auth.employee?.id ? String(auth.employee.id) : null) : employeeParam}
                  onChange={(value) => updateParams({ employee: value && value !== String(auth.employee?.id) ? value : null, highlight: null })}
                  placeholder="Whose timesheet"
                  loading={employeesQuery.isLoading}
                  cleanable={false}
                  renderValue={(value, item) => (
                    <span><i className="bi bi-person me-2" aria-hidden="true" />{item?.label || 'Me'}</span>
                  )}
                  block
                  preventOverflow
                />
              </div>
            ) : null}
            <button
              type="button"
              className="ts-toolicon"
              onClick={() => {
                entriesQuery.refetch();
                periodQuery.refetch();
              }}
              aria-label="Refresh"
              title="Refresh"
            >
              <i className={`bi bi-arrow-repeat${entriesQuery.isFetching ? ' spin' : ''}`} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="ts-rangebar__row">
          <DateStepper
            date={date}
            label={label}
            unit={view === 'period' ? 'pay period' : view}
            onStep={step}
            onJump={goTo}
            onToday={() => goTo(today)}
            showToday={date !== today}
            slideKey={slideKey}
            slideDir={slideDir}
          />
          <dl className="ts-rangebar__stats">
            {stats.map((stat) => (
              <div key={stat.label} className="ts-rangebar__stat">
                <dt>{stat.label}</dt>
                <dd>{formatHM(stat.seconds)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {loadError ? <Message type="error" showIcon>{String(loadError?.message || loadError)}</Message> : null}

      <div className="ts-layout">
        <div className="ts-layout__main">
          {statusBanner ? (
            <Banner tone={statusBanner.tone} icon={statusBanner.icon} title={`Pay period ${formatPeriod(period)}`}>
              {statusBanner.text}
            </Banner>
          ) : null}

          <SlideSwap swapKey={slideKey} direction={slideDir}>
            {entriesQuery.isLoading || tasksQuery.isLoading ? (
              <div className="ts-empty"><i className="bi bi-hourglass-split" aria-hidden="true" />Loading timesheet…</div>
            ) : view === 'day' ? (
              <DayView
                entries={entriesForDate(date)}
                tasks={tasks}
                taskById={taskById}
                config={config}
                editable={editable}
                highlightTaskId={searchParams.get('highlight')}
                dayIssues={issues.allByDate.get(date) || []}
                entryIssues={issues.byEntry}
                onFix={applyFix}
                fixing={fixing}
                onDuplicate={duplicateEntry}
                showHistory={auth.isAdmin()}
              />
            ) : view === 'week' ? (
              <WeekColumns
                days={visibleDays}
                today={today}
                entries={visibleEntries}
                taskById={taskById}
                editable={editable}
                onOpen={openCell}
                onLog={(d) => setModal({ seed: seedFor(d) })}
                onDayClick={(d) => updateParams({ view: null, date: d === today ? null : d, highlight: null })}
                dayFlags={issues.flagsByDate}
              />
            ) : (
              <PeriodGrid
                days={visibleDays}
                today={today}
                rowTaskIds={rowTaskIds}
                extraTaskIds={extraTaskIds}
                taskById={taskById}
                cellMap={cellMap}
                tasks={tasks}
                editable={editable}
                onCellClick={openCell}
                onAddRow={(taskId) => setExtraTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]))}
                onRemoveRow={(taskId) => setExtraTaskIds((prev) => prev.filter((id) => id !== taskId))}
                onCopyPrevious={copyPreviousRows}
                copyingPrevious={previousTasks.isPending}
                onDayClick={(d) => updateParams({ view: null, date: d === today ? null : d, highlight: null })}
                dayFlags={issues.flagsByDate}
              />
            )}
          </SlideSwap>

          <ValidationBanners issues={issues.period} />

          {periodData?.events?.length ? (
            <Section title="Approval History" count={periodData.events.length} collapsible>
              <ul className="ts-checklist">
                {periodData.events.map((ev, idx) => (
                  <li key={`${ev.at}-${idx}`}>
                    <i className="bi bi-dot" aria-hidden="true" />
                    <span>
                      <strong className="text-capitalize">{ev.action}</strong>
                      {ev.by ? ` by ${ev.by}` : ''} · {formatTimestamp(ev.at)}
                      {ev.comment ? <> — “{ev.comment}”</> : null}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>

        <aside className="ts-layout__side" aria-label="Log time and pay period">
          {installed && isSelf && (editable || timerQuery.data?.timer) ? (
            <LogTimeBar tasks={tasks} config={config} date={view === 'day' ? date : (date >= visibleDays[0] && date <= visibleDays[visibleDays.length - 1] ? date : visibleDays[0])} entriesForDate={entriesForDate} />
          ) : null}
          {periodData ? (
            <PeriodCard
              period={period}
              status={status}
              seconds={periodSeconds}
              billableSeconds={periodBillableSeconds}
              missingDays={status === 'approved' ? [] : missingDays}
              issueCount={status === 'approved' ? 0 : issueCount}
              failCount={issues.failCount}
              actions={actions}
              onAction={setActionKey}
            />
          ) : null}
        </aside>
      </div>

      <EntryModal
        open={Boolean(modal)}
        onClose={() => setModal(null)}
        entry={modal?.entry || null}
        seed={modal?.seed || null}
        employeeId={employeeId}
        tasks={tasks}
        config={config}
        readOnly={!editable}
      />

      <PeriodActionModal
        open={Boolean(actionKey)}
        onClose={() => {
          setActionKey(null);
          setActionError(null);
        }}
        onConfirm={runAction}
        loading={periodAction.isPending}
        error={actionError}
        {...PERIOD_ACTIONS[actionKey]}
      >
        <p className="mb-0">
          {actionKey === 'submit'
            ? `You are submitting ${formatHM(periodSeconds)} for ${formatPeriod(period)}. Once submitted, the time is locked until it is reviewed.`
            : actionKey === 'approve'
              ? `Approve ${formatHM(periodSeconds)} for ${formatPeriod(period)}? Approved time is locked.`
              : actionKey === 'return'
                ? 'The timesheet goes back to its owner to fix and resubmit.'
                : 'Reopening unlocks the time so it can be edited and submitted again.'}
        </p>
        {actionKey === 'submit' && issueCount ? (
          <Message type={issues.failCount ? 'error' : 'warning'} showIcon>
            {issueCount} check{issueCount === 1 ? '' : 's'} flagged on this period - the same ones View Hours
            runs. The marked days show what each one is.
          </Message>
        ) : null}
        {actionKey === 'submit' && missingDays.length ? (
          <Message type="warning" showIcon>
            {missingDays.length} weekday{missingDays.length === 1 ? ' has' : 's have'} nothing logged.
          </Message>
        ) : null}
      </PeriodActionModal>
    </div>
  );
}
